import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Heart, Eye, ShoppingCart } from 'lucide-react';

const ProductGrid = ({ products }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-24 h-24 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-3xl">📦</span>
        </div>
        <h3 className="text-2xl font-semibold text-white mb-4">
          No products found
        </h3>
        <p className="text-dark-300">
          Try adjusting your filters or browse all products.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
    >
      {products.map((product) => (
        <motion.div
          key={product.id}
          variants={itemVariants}
          className="group cursor-pointer"
        >
          <div className="bg-dark-800/30 backdrop-blur-md rounded-2xl overflow-hidden border border-dark-700 hover:border-accent-500/30 transition-all duration-500 hover:scale-105">
            {/* Product Image */}
            <div className="relative aspect-square overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300"></div>
              
              {/* Floating Actions */}
              <div className="absolute top-4 right-4 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300 group/btn">
                  <Heart className="h-4 w-4 text-white" />
                </button>
                <Link
                  to={`/products/${product.id}`}
                  className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300"
                >
                  <Eye className="h-4 w-4 text-white" />
                </Link>
              </div>

              {/* Sale Badge */}
              {product.originalPrice > product.price && (
                <div className="absolute top-4 left-4">
                  <span className="bg-accent-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Sale
                  </span>
                </div>
              )}

              {/* Stock Badge */}
              {!product.inStock && (
                <div className="absolute top-4 left-4">
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Out of Stock
                  </span>
                </div>
              )}

              {/* Price Tag */}
              <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="bg-black/50 backdrop-blur-md px-3 py-2 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-bold">${product.price.toLocaleString()}</span>
                    {product.originalPrice > product.price && (
                      <span className="text-dark-400 text-sm line-through">
                        ${product.originalPrice.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Add to Cart */}
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button className="w-10 h-10 bg-accent-500 hover:bg-accent-600 rounded-full flex items-center justify-center transition-colors duration-300">
                  <ShoppingCart className="h-4 w-4 text-white" />
                </button>
              </div>
            </div>

            {/* Product Info */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-accent-400 text-sm font-medium capitalize">
                  {product.category}
                </span>
                <div className="flex items-center space-x-1">
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="text-sm text-dark-300">{product.rating}</span>
                </div>
              </div>
              
              <Link to={`/products/${product.id}`}>
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-accent-400 transition-colors duration-300 line-clamp-2">
                  {product.name}
                </h3>
              </Link>
              
              <p className="text-dark-300 text-sm mb-4 leading-relaxed line-clamp-2">
                {product.description}
              </p>

              {/* Features */}
              {product.features && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {product.features.slice(0, 2).map((feature, featureIndex) => (
                      <span
                        key={featureIndex}
                        className="text-xs bg-dark-700 text-dark-300 px-2 py-1 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                    {product.features.length > 2 && (
                      <span className="text-xs text-accent-400">
                        +{product.features.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Price and Stock */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-xl font-bold text-white">
                    ${product.price.toLocaleString()}
                  </span>
                  {product.originalPrice > product.price && (
                    <span className="text-dark-400 text-sm line-through">
                      ${product.originalPrice.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className={`text-xs font-medium px-2 py-1 rounded-full ${
                  product.inStock 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to={`/products/${product.id}`}
                  className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-2 px-4 rounded-lg font-medium text-sm hover:from-accent-600 hover:to-accent-700 transition-all duration-300 text-center"
                >
                  View Details
                </Link>
                <button 
                  className={`border border-accent-500 text-accent-400 py-2 px-4 rounded-lg font-medium text-sm transition-all duration-300 ${
                    product.inStock 
                      ? 'hover:bg-accent-500/10' 
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!product.inStock}
                >
                  {product.inStock ? 'Add to Cart' : 'Unavailable'}
                </button>
              </div>

              {/* Reviews Preview */}
              {product.reviews > 0 && (
                <div className="mt-4 pt-4 border-t border-dark-700">
                  <div className="text-xs text-dark-400">
                    Based on {product.reviews} customer review{product.reviews !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default ProductGrid;